import React, { useEffect, useState } from 'react';
import { reportAPI } from '../services/api';

const today = new Date().toISOString().split('T')[0];
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const FIELD_LABELS = {
  cashCollected: 'Cash collected',
  emiCollected: 'EMI installments collected',
  cashInHand: 'Cash in hand (end of period)'
};

function weekLabel(rangeStart, rangeEnd) {
  if (!rangeStart || !rangeEnd) return '';
  const a = new Date(rangeStart);
  const b = new Date(rangeEnd);
  const opts = { day: '2-digit', month: 'short' };
  return `${a.toLocaleDateString('en-IN', opts)} – ${b.toLocaleDateString('en-IN', { ...opts, year: 'numeric' })}`;
}

export default function DailyReport() {
  const [reportKind, setReportKind] = useState('weekly');
  const [date, setDate] = useState(today);
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ cashCollected: 0, emiCollected: 0, cashInHand: 0, notes: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const load = async (d = date, kind = reportKind) => {
    const [r, h] = await Promise.all([reportAPI.get(d, kind), reportAPI.history()]);
    const rep = r.data.report;
    setReport(rep);
    setPeriod(r.data.period || null);
    setHistory(h.data.reports || []);
    if (rep?.managerData) setForm(rep.managerData);
  };

  useEffect(() => { load(date, reportKind); }, []); // eslint-disable-line

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const switchKind = (k) => {
    setReportKind(k);
    load(date, k);
  };

  const save = async (submit = false) => {
    setSaving(true);
    try {
      const payload = { reportDate: date, reportKind, managerData: form };
      if (submit) await reportAPI.submit(payload);
      else await reportAPI.save(payload);
      await load(date, reportKind);
      flash(submit ? 'Report submitted successfully.' : 'Draft saved.');
    } catch (e) {
      flash(e.response?.data?.message || 'Save failed. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const statusChip = (s) => {
    if (s === 'submitted') return 'chip badge-kyc-complete';
    if (s === 'draft') return 'chip badge-pending';
    return 'chip badge-active';
  };

  const periodTitle = reportKind === 'weekly' ? 'Weekly report' : 'Single day';
  const systemTitleDate = period
    ? (reportKind === 'weekly'
        ? weekLabel(period.rangeStart, period.rangeEnd)
        : new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }))
    : date;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h4 className="mb-1 fw-700">Branch report</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Weekly summary recommended · optional single-day entry if needed
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${reportKind === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => switchKind('weekly')}
            >
              Weekly
            </button>
            <button
              type="button"
              className={`btn btn-sm ${reportKind === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => switchKind('daily')}
            >
              Single day
            </button>
          </div>
          <input
            type="date"
            className="form-control"
            style={{ maxWidth: 180 }}
            title={reportKind === 'weekly' ? 'Any date in the week — week is calculated Mon–Sun' : 'Report date'}
            value={date}
            onChange={e => {
              const v = e.target.value;
              setDate(v);
              load(v, reportKind);
            }}
          />
        </div>
      </div>

      {toast && <div className="alert-soft success mb-3">{toast}</div>}

      <div className="alert-soft info mb-3" style={{ fontSize: 13 }}>
        <strong>{periodTitle}:</strong>{' '}
        {reportKind === 'weekly'
          ? 'Poore hafte ka system total auto-fill hota hai. Neeche apna cash / EMI collection aur cash in hand bhar ke submit karein.'
          : 'Sirf us din ka data jab aap rozana bhejna chahein.'}
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          {report?.systemData && (
            <div className="mb-3">
              <div className="section-head blue">
                System totals — {systemTitleDate}
              </div>
              <div className="section-body">
                <div className="row g-2">
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Total sales</div>
                    <div className="fw-700">{report.systemData.totalSalesCount || 0} sales</div>
                  </div>
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Total billed</div>
                    <div className="fw-700 text-success">{fmt(report.systemData.totalSalesAmount)}</div>
                  </div>
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Cash sales</div>
                    <div className="fw-700">{fmt(report.systemData.cashSalesAmount)}</div>
                  </div>
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: '#6b7280' }}>EMI down payments</div>
                    <div className="fw-700">{fmt(report.systemData.upiSalesAmount || 0)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="section-head green">
            Manager entry
            {report?.status && (
              <span className={`ms-2 ${statusChip(report.status)}`}>{report.status}</span>
            )}
          </div>
          <div className="section-body">
            <div className="row g-3">
              {['cashCollected', 'emiCollected', 'cashInHand'].map(k => (
                <div className="col-md-6" key={k}>
                  <label className="form-label">
                    {FIELD_LABELS[k] || k}
                    {reportKind === 'weekly' && (
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>(week)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={form[k] || 0}
                    onChange={e => setForm({ ...form, [k]: Number(e.target.value) })}
                  />
                </div>
              ))}
              <div className="col-12">
                <label className="form-label">Notes / remarks</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={reportKind === 'weekly' ? 'Week summary, issues, handover notes…' : 'Observations for this day…'}
                  value={form.notes || ''}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-outline-primary" onClick={() => save(false)} disabled={saving}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button className="btn btn-primary" onClick={() => save(true)} disabled={saving}>
                {saving ? 'Submitting…' : reportKind === 'weekly' ? 'Submit weekly report' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="section-head">History</div>
          <div className="section-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Cash in hand</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const rk = h.reportKind || 'daily';
                    const rd = h.reportDate ? h.reportDate.split('T')[0] : '';
                    return (
                      <tr
                        key={h._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDate(rd);
                          setReportKind(rk);
                          load(rd, rk);
                        }}
                      >
                        <td style={{ fontSize: 12 }}>
                          {new Date(h.reportDate).toLocaleDateString('en-IN')}
                        </td>
                        <td>
                          <span className={rk === 'weekly' ? 'chip badge-active' : 'chip badge-pending'}>
                            {rk === 'weekly' ? 'Weekly' : 'Day'}
                          </span>
                        </td>
                        <td><span className={statusChip(h.status)}>{h.status}</span></td>
                        <td>{fmt(h.managerData?.cashInHand)}</td>
                      </tr>
                    );
                  })}
                  {history.length === 0 && (
                    <tr><td colSpan="4" className="text-center py-3 text-muted">No history</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
